import { formatCelular } from "./inputMasks";

const ESCALA_LABELS = {
  1: "Muito baixo",
  2: "Baixo",
  3: "Médio",
  4: "Alto",
  5: "Muito alto",
};

const findOpcaoTexto = (opcoes, valor) => {
  const opcao = (opcoes || []).find((o) => o.valor === valor);
  return opcao ? opcao.texto : valor;
};

const buildOpcoesStatus = (opcoes, selectedValue) => {
  const selectedList = Array.isArray(selectedValue)
    ? selectedValue
    : selectedValue !== undefined && selectedValue !== null && selectedValue !== ""
      ? [selectedValue]
      : [];

  return (opcoes || []).map((opcao) => ({
    valor: opcao.valor,
    texto: opcao.texto,
    tooltip: opcao.tooltip || null,
    selecionada: selectedList.includes(opcao.valor),
    exclusiva: !!opcao.exclusiva,
  }));
};

const formatMultiSelect = (opcoes, rawValue) => {
  const valores = Array.isArray(rawValue) ? rawValue : [];
  return {
    opcoes: buildOpcoesStatus(opcoes, valores),
    valor: valores.length > 0 ? valores : null,
    resposta:
      valores.length > 0
        ? valores.map((v) => findOpcaoTexto(opcoes, v))
        : null,
  };
};

const formatSingleSelect = (opcoes, rawValue) => {
  const hasValue =
    rawValue !== undefined && rawValue !== null && rawValue !== "";

  return {
    opcoes: buildOpcoesStatus(opcoes, hasValue ? rawValue : null),
    valor: hasValue ? rawValue : null,
    resposta: hasValue
      ? findOpcaoTexto(opcoes, rawValue) || String(rawValue)
      : null,
  };
};

const formatAnswer = (question, rawValue) => {
  if (question.tipo === "multipla_escolha") {
    return formatMultiSelect(question.opcoes, rawValue);
  }

  if (
    question.tipo === "unica_escolha" ||
    question.tipo === "boolean" ||
    question.tipo === "escolha"
  ) {
    return formatSingleSelect(question.opcoes, rawValue);
  }

  if (question.tipo === "escala" || question.tipo === "numero") {
    if (question.tipo === "escala") {
      return formatEscala(question, rawValue);
    }

    const hasValue =
      rawValue !== undefined && rawValue !== null && rawValue !== "";
    const numeric = Number(rawValue);
    const escalaLabel = ESCALA_LABELS[numeric];

    return {
      valor: hasValue ? rawValue : null,
      resposta: hasValue ? escalaLabel || String(rawValue) : null,
    };
  }

  const hasValue =
    rawValue !== undefined && rawValue !== null && rawValue !== "";

  return {
    valor: hasValue ? rawValue : null,
    resposta: hasValue ? String(rawValue) : null,
  };
};

const formatEscala = (bloco, rawValue) => {
  const hasValue =
    rawValue !== undefined && rawValue !== null && rawValue !== "";
  const min = bloco.min ?? 1;
  const max = bloco.max ?? 5;
  const isExclusiva =
    hasValue &&
    bloco.opcaoExclusiva &&
    rawValue === bloco.opcaoExclusiva.valor;

  const opcoes = [];
  for (let score = min; score <= max; score += 1) {
    opcoes.push({
      valor: score,
      texto: ESCALA_LABELS[score],
      tooltip: null,
      selecionada: hasValue && Number(rawValue) === score,
    });
  }

  if (bloco.opcaoExclusiva) {
    opcoes.push({
      valor: bloco.opcaoExclusiva.valor,
      texto: bloco.opcaoExclusiva.texto,
      tooltip: bloco.opcaoExclusiva.tooltip || null,
      selecionada: isExclusiva,
      exclusiva: true,
    });
  }

  return {
    opcoes,
    valor: hasValue ? rawValue : null,
    resposta: hasValue
      ? isExclusiva
        ? bloco.opcaoExclusiva.texto
        : ESCALA_LABELS[Number(rawValue)] || String(rawValue)
      : null,
  };
};

const formatBlockAnswer = (bloco, rawValue) => {
  if (bloco.tipo === "multipla_escolha") {
    return formatMultiSelect(bloco.opcoes, rawValue);
  }

  if (bloco.tipo === "unica_escolha") {
    return formatSingleSelect(bloco.opcoes, rawValue);
  }

  if (bloco.tipo === "escala") {
    return formatEscala(bloco, rawValue);
  }

  if (bloco.tipo === "celular") {
    const hasValue =
      rawValue !== undefined && rawValue !== null && rawValue !== "";
    const compact = hasValue ? formatCelular(rawValue) : null;

    return {
      valor: compact,
      resposta: compact,
    };
  }

  if (bloco.tipo === "numero" && bloco.opcaoExclusiva) {
    const hasValue =
      rawValue !== undefined && rawValue !== null && rawValue !== "";
    const isExclusiva = hasValue && rawValue === bloco.opcaoExclusiva.valor;

    if (isExclusiva) {
      return {
        valor: rawValue,
        resposta: bloco.opcaoExclusiva.texto,
      };
    }

    if (hasValue) {
      return {
        valor: rawValue,
        resposta: String(rawValue),
      };
    }

    if (bloco.valorPadrao !== undefined) {
      const effective = String(bloco.valorPadrao);
      return {
        valor: effective,
        resposta: effective,
      };
    }

    return {
      valor: null,
      resposta: null,
    };
  }

  if (bloco.tipo === "numero" && bloco.valorPadrao !== undefined) {
    const hasValue =
      rawValue !== undefined && rawValue !== null && rawValue !== "";
    const effective = hasValue ? rawValue : String(bloco.valorPadrao);

    return {
      valor: effective,
      resposta: String(effective),
    };
  }

  const hasValue =
    rawValue !== undefined && rawValue !== null && rawValue !== "";

  return {
    valor: hasValue ? rawValue : null,
    resposta: hasValue ? String(rawValue) : null,
  };
};

const buildQuestionEntry = (
  question,
  passo,
  answers,
  conditionalAnswers,
  stepCompletedAt,
  cnpjData
) => {
  const answer = answers[question.id];
  const entry = {
    passo,
    perguntaId: question.id,
    pergunta: question.texto,
    label: question.label || null,
    tipo: question.tipo,
    obrigatoria: !!question.obrigatoria,
    finalizadoEm: stepCompletedAt[question.id] || null,
  };

  if (question.tipo === "multiplos_blocos") {
    const blockAnswers = answer && typeof answer === "object" ? answer : {};
    entry.blocos = (question.blocos || []).map((bloco, blockIndex) => ({
      id: bloco.id,
      label:
        bloco.texto ||
        (blockIndex === 0 ? question.texto : null) ||
        null,
      tipo: bloco.tipo,
      obrigatoria: !!bloco.obrigatoria,
      exclusiva: !!bloco.exclusiva,
      opcaoExclusiva: bloco.opcaoExclusiva || null,
      tooltip: bloco.tooltip || null,
      ...formatBlockAnswer(bloco, blockAnswers[bloco.id]),
    }));
    return entry;
  }

  Object.assign(entry, formatAnswer(question, answer));

  if (question.condicao) {
    const condValue = conditionalAnswers[question.id];
    const condTriggered = answer === question.condicao.se;
    const hasCondValue =
      condValue !== undefined && condValue !== null && String(condValue).trim() !== "";

    entry.condicional = {
      pergunta: question.condicao.pergunta_adicional?.texto || null,
      tipo: question.condicao.pergunta_adicional?.tipo || null,
      obrigatoria: !!question.condicao.pergunta_adicional?.obrigatoria,
      disparada: condTriggered,
      valor: condTriggered && hasCondValue ? condValue : null,
      resposta: condTriggered && hasCondValue ? String(condValue) : null,
    };
  }

  if (question.tipo === "cnpj") {
    entry.dadosCnpj = cnpjData
      ? {
          razao_social: cnpjData.razao_social || null,
          nome_fantasia: cnpjData.nome_fantasia || null,
        }
      : {
          razao_social: null,
          nome_fantasia: null,
        };
  }

  return entry;
};

export function buildDiagnosisPayload({
  perguntas,
  answers,
  conditionalAnswers,
  stepCompletedAt,
  submittedAt,
  inicioEm = null,
  origem = "",
  cnpjData = null,
  aceitePoliticaPrivacidade = false,
  aceitePoliticaPrivacidadeEm = null,
}) {
  return {
    inicioEm,
    origem,
    solicitadoEm: submittedAt,
    aceitePoliticaPrivacidade: !!aceitePoliticaPrivacidade,
    aceitePoliticaPrivacidadeEm: aceitePoliticaPrivacidade
      ? aceitePoliticaPrivacidadeEm || inicioEm || submittedAt
      : null,
    passos: perguntas.map((question, index) =>
      buildQuestionEntry(
        question,
        index + 1,
        answers,
        conditionalAnswers,
        stepCompletedAt,
        question.tipo === "cnpj" ? cnpjData : null
      )
    ),
  };
};
